import { normalizeTitle } from './api';
import type { ArticleDoc, Block, ImageRef, InfoRow, Run } from './types';

/** Back-matter sections that are all citations/external links: not fun to platform through. */
const SKIP_SECTIONS = new Set(
  [
    'references', 'notes', 'footnotes', 'citations', 'sources', 'bibliography', 'further reading',
    'external links', 'works cited', 'notes and references', 'references and notes', 'explanatory notes',
    'general sources', 'cited sources', 'general and cited sources', 'primary sources', 'secondary sources',
    'informational notes', 'general references', 'specific references', 'inline citations', 'cited works',
    'general bibliography', 'selected bibliography', 'reference notes', 'gallery', 'external link',
    'notes and citations', 'sources and further reading', 'citations and notes', 'endnotes', 'literature',
  ],
);

const NAMESPACES = new Set(
  [
    'file', 'image', 'media', 'category', 'help', 'wikipedia', 'wp', 'project', 'template', 'special',
    'portal', 'talk', 'user', 'draft', 'module', 'mediawiki', 'book', 'timedtext', 'gadget',
    'user talk', 'wikipedia talk', 'file talk', 'template talk', 'help talk', 'category talk',
    'portal talk', 'draft talk', 'module talk', 'wikt', 'wiktionary', 'commons', 'wikisource', 's',
    'wikiquote', 'q', 'meta', 'm', 'mw', 'species', 'wikinews', 'n', 'wikivoyage', 'voy', 'wikiversity',
    'v', 'wikibooks', 'b', 'wikidata', 'd', 'c', 'w', 'foundation', 'wmf', 'phab', 'arxiv', 'doi',
  ],
);

const SKIP_TAGS = new Set([
  'style', 'script', 'link', 'meta', 'img', 'audio', 'video', 'source', 'figure', 'table', 'input', 'button',
  'svg', 'math', 'noscript', 'map', 'area', 'object', 'iframe', 'wbr',
]);

const SKIP_CLASS = /\b(mw-editsection|noprint|reference|mw-ref|sortkey|Z3988|mw-cite-backlink|metadata|navbox|ext-phonos|mw-empty-elt|mw-references-wrap|reflist|geo-nondefault|geo-multi-punct|mwe-math-mathml-a11y|nowrap-hidden|sr-only|visually-hidden|mw-kartographer-maplink|shortdescription|haudio|IPA-label)\b/;

export function isNamespaced(title: string): boolean {
  const i = title.indexOf(':');
  return i > 0 && NAMESPACES.has(title.slice(0, i).trim().toLowerCase());
}

interface LinkInfo {
  title: string;
  red: boolean;
  redirect: boolean;
}

function classOf(el: Element): string {
  return el.getAttribute('class') ?? '';
}

export function linkInfo(a: Element): LinkInfo | null {
  const href = a.getAttribute('href') ?? '';
  const cls = classOf(a);
  if (/\bnew\b/.test(cls)) {
    const m = /[?&]title=([^&]+)/.exec(href);
    let t = '';
    try {
      t = m ? decodeURIComponent(m[1]) : (a.getAttribute('title') ?? '').replace(/\s*\(page does not exist\)$/, '');
    } catch {
      return null;
    }
    t = normalizeTitle(t);
    return t && !isNamespaced(t) ? { title: t, red: true, redirect: false } : null;
  }
  if (!href.startsWith('/wiki/')) return null;
  if (/\b(extiw|external|mw-selflink|mw-file-description|image)\b/.test(cls)) return null;
  let t = '';
  try {
    t = decodeURIComponent(href.slice(6).split('#')[0]);
  } catch {
    return null;
  }
  t = normalizeTitle(t);
  if (!t || isNamespaced(t)) return null;
  return { title: t, red: false, redirect: /\bmw-redirect\b/.test(cls) };
}

interface Style {
  bold: boolean;
  italic: boolean;
  link?: string;
  red?: boolean;
  redirect?: boolean;
}

const PLAIN: Style = { bold: false, italic: false };

function pushText(out: Run[], text: string, st: Style): void {
  if (!text) return;
  const r: Run = { text };
  if (st.bold) r.bold = true;
  if (st.italic) r.italic = true;
  if (st.link) {
    r.link = st.link;
    if (st.red) r.red = true;
    if (st.redirect) r.redirect = true;
  }
  out.push(r);
}

function mathText(el: Element): string {
  const alt = el.querySelector('img')?.getAttribute('alt') ?? el.querySelector('annotation')?.textContent ?? '';
  const cleaned = alt
    .replace(/\\displaystyle|\\textstyle|\\mathrm|\\operatorname|\\left|\\right/g, '')
    .replace(/[{}\\]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned && cleaned.length <= 24 ? cleaned : '[formula]';
}

function walk(node: Node, st: Style, out: Run[], lists: boolean): void {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === 3) {
      pushText(out, child.nodeValue ?? '', st);
      continue;
    }
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const tag = el.localName;
    const cls = classOf(el);
    if (tag === 'sup' && /\breference\b/.test(cls)) {
      const label = (el.textContent ?? '').replace(/[[\]]/g, '').trim();
      if (label) out.push({ text: label, cite: true });
      continue;
    }
    if (/\bTemplate-Fact\b/.test(cls)) {
      out.push({ text: 'citation needed', cn: true });
      continue;
    }
    if (/\bmwe-math-element\b/.test(cls)) {
      pushText(out, ` ${mathText(el)} `, st);
      continue;
    }
    if (SKIP_TAGS.has(tag) || SKIP_CLASS.test(cls)) continue;
    const style = el.getAttribute('style') ?? '';
    if (/display\s*:\s*none/i.test(style)) continue;
    switch (tag) {
      case 'br':
        pushText(out, ' ', st);
        continue;
      case 'a': {
        const info = linkInfo(el);
        walk(el, info ? { ...st, link: info.title, red: info.red, redirect: info.redirect } : st, out, lists);
        continue;
      }
      case 'b':
      case 'strong':
        walk(el, { ...st, bold: true }, out, lists);
        continue;
      case 'i':
      case 'em':
      case 'var':
      case 'cite':
      case 'dfn':
        walk(el, { ...st, italic: true }, out, lists);
        continue;
      case 'ul':
      case 'ol': {
        if (!lists) continue;
        let first = true;
        for (const li of Array.from(el.children)) {
          if (li.localName !== 'li') continue;
          if (!first) pushText(out, ' · ', PLAIN);
          walk(li, st, out, lists);
          first = false;
        }
        continue;
      }
      case 'li':
      case 'div':
      case 'p':
      case 'dd':
      case 'dt':
      case 'blockquote':
        pushText(out, ' ', PLAIN);
        walk(el, st, out, lists);
        pushText(out, ' ', PLAIN);
        continue;
      default:
        walk(el, st, out, lists);
    }
  }
}

export function inlineRuns(el: Element, lists = false): Run[] {
  const out: Run[] = [];
  walk(el, PLAIN, out, lists);
  return out;
}

export function runsText(runs: Run[]): string {
  return runs
    .filter((r) => !r.cite && !r.cn)
    .map((r) => r.text)
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

function hasText(runs: Run[]): boolean {
  return runs.some((r) => !r.cite && !r.cn && /[\p{L}\p{N}]/u.test(r.text));
}

function imageRef(img: Element | null | undefined, minSize = 60): ImageRef | undefined {
  if (!img) return undefined;
  const w = Number(img.getAttribute('width'));
  const h = Number(img.getAttribute('height'));
  let src = img.getAttribute('src') ?? '';
  if (!src || !w || !h || w < minSize || h < minSize / 2) return undefined;
  // Prefer the 2x rendition for crispness when available.
  const srcset = img.getAttribute('srcset') ?? '';
  const two = /(\S+)\s+2x/.exec(srcset) ?? /(\S+)\s+1\.5x/.exec(srcset);
  if (two) src = two[1];
  if (src.startsWith('//')) src = `https:${src}`;
  else if (src.startsWith('/')) src = `https://upload.wikimedia.org${src}`;
  return { src, width: w, height: h };
}

/** Text of the first "line" of a cell (up to the first <br> or block element). */
function firstLine(el: Element): string {
  let s = '';
  for (const n of Array.from(el.childNodes)) {
    if (n.nodeType === 3) s += n.nodeValue;
    else if (n.nodeType === 1) {
      const t = (n as Element).localName;
      if (t === 'br' || t === 'div' || t === 'p' || t === 'ul' || t === 'table') break;
      if (SKIP_TAGS.has(t) || SKIP_CLASS.test(classOf(n as Element))) continue;
      s += n.textContent;
    }
  }
  return s.replace(/\s+/g, ' ').trim();
}

function rowsOf(table: Element): Element[] {
  const rows: Element[] = [];
  for (const c of Array.from(table.children)) {
    if (c.localName === 'tr') rows.push(c);
    else if (c.localName === 'tbody' || c.localName === 'thead')
      for (const r of Array.from(c.children)) if (r.localName === 'tr') rows.push(r);
  }
  return rows;
}

function extractInfobox(table: Element, fallbackTitle: string): Block | null {
  const rows: InfoRow[] = [];
  let image: ImageRef | undefined;
  let title = '';
  for (const tr of rowsOf(table)) {
    const cells = Array.from(tr.children).filter((c) => c.localName === 'th' || c.localName === 'td');
    if (!cells.length) continue;
    if (!image) {
      const ref = imageRef(tr.querySelector('img'), 80);
      if (ref) {
        image = ref;
        continue;
      }
    }
    if (cells.length === 1) {
      const c = cells[0];
      const cls = classOf(c);
      if (!title && (/infobox-above|infobox-title/.test(cls) || (c.localName === 'th' && rows.length === 0))) {
        title = firstLine(c);
        continue;
      }
      if (c.localName === 'th' || /infobox-header/.test(cls)) {
        const t = runsText(inlineRuns(c));
        if (t && t.length <= 48) rows.push({ label: t, value: [], header: true });
      }
      continue;
    }
    const label = runsText(inlineRuns(cells[0])).replace(/:\s*$/, '');
    const value = inlineRuns(cells[1], true);
    if (label && label.length <= 40 && hasText(value)) rows.push({ label, value });
  }
  // Drop section headers that ended up with nothing under them.
  const cleaned = rows.filter((r, i) => !r.header || (rows[i + 1] && !rows[i + 1].header));
  while (cleaned.length && cleaned[cleaned.length - 1].header) cleaned.pop();
  if (!cleaned.length && !image) return null;
  if (!title || title.length > 60) title = fallbackTitle;
  return { kind: 'infobox', title, image, rows: cleaned.slice(0, 26) };
}

function extractFigure(el: Element): Block | null {
  const image = imageRef(el.querySelector('img'));
  if (!image) return null;
  const cap = el.querySelector('figcaption') ?? el.querySelector('.thumbcaption');
  const caption = cap ? inlineRuns(cap) : [];
  const cls = classOf(el);
  const align = /mw-halign-left|\btleft\b|floatleft/.test(cls)
    ? 'left'
    : /mw-halign-center|\btnone\b|\bcenter\b/.test(cls)
      ? 'center'
      : 'right';
  return { kind: 'figure', image, align, caption };
}

const CONTAINER_CLASS = /^(|div-col|excerpt-block|excerpt|mw-content-ltr|mw-content-rtl|mw-parser-output|columns|mw-collapsible.*|refbegin)$/;
const SKIP_LIST_CLASS = /\b(gallery|portalbox|noprint|references|mw-gallery|navbox|sistersitebox)\b/;

export interface ExtractMeta {
  title: string;
  displayTitle?: string;
  description?: string;
  pageId?: number;
  revId?: number;
  lang?: string;
}

export function extractArticle(html: string, meta: ExtractMeta): ArticleDoc {
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${html}</body>`, 'text/html');
  const root = doc.querySelector('.mw-parser-output') ?? doc.body;
  const blocks: Block[] = [];
  let skipLevel = 0;
  let infobox = false;

  const para = (runs: Run[], indent: number, style: 'normal' | 'hatnote' | 'quote', bullet?: string) => {
    if (hasText(runs)) blocks.push({ kind: 'para', runs, indent, bullet, style });
  };

  const list = (el: Element, depth: number) => {
    const ordered = el.localName === 'ol';
    let n = 1;
    for (const li of Array.from(el.children)) {
      if (li.localName !== 'li') continue;
      const runs = inlineRuns(li);
      para(runs, Math.min(depth + 1, 4), 'normal', ordered ? `${n}.` : '•');
      n++;
      for (const sub of Array.from(li.children))
        if ((sub.localName === 'ul' || sub.localName === 'ol') && !SKIP_LIST_CLASS.test(classOf(sub)))
          list(sub, depth + 1);
    }
  };

  /** Data tables (common in list articles) become one line per row: "cell · cell · cell". */
  const tableRows = (table: Element) => {
    const rows = rowsOf(table);
    let emitted = 0;
    for (const tr of rows) {
      const cells = Array.from(tr.children).filter((c) => c.localName === 'td' || c.localName === 'th');
      if (!cells.length || cells.every((c) => c.localName === 'th')) continue;
      const runs: Run[] = [];
      for (const c of cells) {
        const cellRuns = inlineRuns(c, true);
        if (!hasText(cellRuns)) continue;
        if (runs.length) runs.push({ text: ' · ' });
        runs.push(...cellRuns);
      }
      if (!hasText(runs)) continue;
      if (emitted === 80) {
        para([{ text: `…and ${rows.length - 80} more rows (see the full table on Wikipedia).` }], 1, 'normal', '⋯');
        break;
      }
      para(runs, 1, 'normal', '▸');
      emitted++;
    }
  };

  const visit = (container: Element) => {
    for (const el of Array.from(container.children)) {
      const tag = el.localName;
      const cls = classOf(el);
      let h: Element | null = null;
      if (/^h[2-6]$/.test(tag)) h = el;
      else if (tag === 'div' && /\bmw-heading\b/.test(cls)) h = el.querySelector('h2,h3,h4,h5,h6');
      if (h) {
        const level = Number(h.localName[1]);
        if (skipLevel && level > skipLevel) continue;
        skipLevel = 0;
        const text = runsText(inlineRuns(h));
        if (SKIP_SECTIONS.has(text.toLowerCase())) {
          skipLevel = level;
          continue;
        }
        if (text) blocks.push({ kind: 'heading', level: Math.min(level, 4), text });
        continue;
      }
      if (skipLevel) continue;
      switch (tag) {
        case 'p':
          para(inlineRuns(el), 0, 'normal');
          break;
        case 'ul':
        case 'ol':
          if (!SKIP_LIST_CLASS.test(cls)) list(el, 0);
          break;
        case 'dl':
          for (const d of Array.from(el.children)) {
            if (d.localName === 'dt') para(inlineRuns(d), 0, 'normal');
            else if (d.localName === 'dd') para(inlineRuns(d), 1, 'normal');
          }
          break;
        case 'blockquote':
          para(inlineRuns(el), 1, 'quote');
          break;
        case 'figure': {
          const f = extractFigure(el);
          if (f) blocks.push(f);
          break;
        }
        case 'table':
          if (!infobox && /\binfobox\b/.test(cls)) {
            const b = extractInfobox(el, meta.title);
            if (b) {
              blocks.push(b);
              infobox = true;
            }
          } else if (/\bwikitable\b/.test(cls) && !/\b(navbox|sidebar|metadata|ambox)\b/.test(cls)) tableRows(el);
          break;
        case 'div':
          if (/\b(hatnote|dablink|rellink)\b/.test(cls)) para(inlineRuns(el), 1, 'hatnote');
          else if (/\bthumb\b/.test(cls) && !/\bthumbinner\b/.test(cls)) {
            const f = extractFigure(el);
            if (f) blocks.push(f);
          } else if (CONTAINER_CLASS.test(cls.trim())) visit(el);
          break;
        case 'section':
          visit(el);
          break;
        default:
          break;
      }
    }
  };

  visit(root);

  return {
    title: meta.title,
    displayTitle: meta.displayTitle || meta.title,
    description: meta.description ?? '',
    pageId: meta.pageId ?? 0,
    revId: meta.revId ?? 0,
    lang: meta.lang ?? 'en',
    blocks,
  };
}
